// Multi-wing 3D scene for U-shaped buildings and the Commercial Plaza row.
// Built with React.createElement to bypass JSX babel instrumentation issues with R3F primitives.
import React, { Suspense, useEffect, useMemo } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { KIND_COLORS_3D, placeRoom, placeDoor } from "./buildingHelpers";

const h = React.createElement;
const F = React.Fragment;

function CameraController({ preset }) {
  const { camera } = useThree();
  useEffect(() => {
    if (!preset) return;
    camera.position.set(preset.pos[0], preset.pos[1], preset.pos[2]);
    camera.lookAt(preset.target[0], preset.target[1], preset.target[2]);
    camera.updateProjectionMatrix();
  }, [preset, camera]);
  return null;
}

// ----- Unit shell + interior preview -----
function unitBox(unit, ut, ceilingFt, slabTop, transparent, showEgress, isActive,
                 hasBalcony, balconyOnFront, key, overrideColor, showUnitLabels = true) {
  const cx = unit.x + ut.length_ft / 2;
  const cz = unit.z + ut.depth_ft / 2;
  const cy = slabTop + ceilingFt / 2;
  const children = [];

  // Container shell — tinted by unit type so 2BR / 1BR / Studio are distinguishable in 3D.
  // (Real containers are white, but the tint stays subtle on residential and full on commercial.)
  const SHELL_TINT = {
    STUDIO_15: "#dcfce7",  // pale green
    ONEBR_25:  "#dbeafe",  // pale blue
    TWOBR_40:  "#fde68a",  // distinctly amber — pops on the elevation
    COMM_40:   overrideColor || ut.color,
  };
  const shellColor = SHELL_TINT[ut.id] || "#f8fafc";
  children.push(
    h("mesh", { key: "shell" },
      h("boxGeometry", { args: [ut.length_ft, ceilingFt, ut.depth_ft] }),
      h("meshStandardMaterial", {
        color: shellColor, metalness: 0.45, roughness: 0.5,
        transparent, opacity: transparent ? 0.18 : 1,
      })
    )
  );
  // Top rail strip (mimics container's corner-cap detailing)
  children.push(
    h("mesh", { key: "topcap", position: [0, ceilingFt / 2 - 0.2, 0] },
      h("boxGeometry", { args: [ut.length_ft + 0.05, 0.35, ut.depth_ft + 0.05] }),
      h("meshStandardMaterial", { color: "#0f172a", metalness: 0.6, roughness: 0.3 })
    )
  );

  if (transparent) {
    ut.rooms.forEach((room, ri) => {
      const r = placeRoom(unit, ut, room);
      const rcx = r.x + r.w / 2 - cx;
      const rcz = r.z + r.d / 2 - cz;
      children.push(
        h("mesh", { key: `room-${ri}`, position: [rcx, -ceilingFt / 2 + 0.06, rcz] },
          h("boxGeometry", { args: [Math.max(r.w - 0.1, 0.1), 0.1, Math.max(r.d - 0.1, 0.1)] }),
          h("meshStandardMaterial", { color: KIND_COLORS_3D[room.kind] || "#334155" })
        )
      );
    });
  }

  // Windows on the two long sides of the unit
  const winCount = Math.max(2, Math.floor(ut.length_ft / 8));
  const winSpacing = ut.length_ft / (winCount + 1);
  const balconyDoorWidth = 6;

  for (let wi = 0; wi < winCount; wi++) {
    const xPos = -ut.length_ft / 2 + winSpacing * (wi + 1);
    // Front face (local -z direction)
    const skipFront = hasBalcony && balconyOnFront && Math.abs(xPos) < balconyDoorWidth / 2 + 0.5;
    const skipBack  = hasBalcony && !balconyOnFront && Math.abs(xPos) < balconyDoorWidth / 2 + 0.5;
    if (!skipFront) {
      children.push(
        h("mesh", { key: `win-f-${wi}`, position: [xPos, 0.5, -ut.depth_ft / 2 - 0.02], rotation: [0, Math.PI, 0] },
          h("planeGeometry", { args: [2.5, 3] }),
          h("meshStandardMaterial", { color: "#7dd3fc", emissive: "#0ea5e9", emissiveIntensity: 0.55, transparent: true, opacity: 0.85 })
        )
      );
    }
    if (!skipBack) {
      children.push(
        h("mesh", { key: `win-b-${wi}`, position: [xPos, 0.5, ut.depth_ft / 2 + 0.02] },
          h("planeGeometry", { args: [2.5, 3] }),
          h("meshStandardMaterial", { color: "#7dd3fc", emissive: "#0ea5e9", emissiveIntensity: 0.55, transparent: true, opacity: 0.85 })
        )
      );
    }
  }

  // Balcony sliding glass door on the courtyard-facing side
  if (hasBalcony) {
    const balconyZ = balconyOnFront ? -ut.depth_ft / 2 - 0.02 : ut.depth_ft / 2 + 0.02;
    const balconyRot = balconyOnFront ? Math.PI : 0;
    children.push(
      h("mesh", { key: "bal-door", position: [0, -ceilingFt / 2 + 3.5, balconyZ], rotation: [0, balconyRot, 0] },
        h("planeGeometry", { args: [balconyDoorWidth, 7] }),
        h("meshStandardMaterial", {
          color: "#bfdbfe", emissive: "#3b82f6", emissiveIntensity: 0.35,
          transparent: true, opacity: 0.6, metalness: 0.6, roughness: 0.2
        })
      )
    );
  }

  // Entry door on the CATWALK side (opposite of balcony)
  if (ut.doors[0]) {
    const d = ut.doors[0];
    const pd = placeDoor(unit, ut, d);
    const dx = pd.x - cx;
    // Force entry to the catwalk side
    const onFront = !balconyOnFront;   // entry on front if balcony on back; vice versa
    const entryZ = onFront ? -ut.depth_ft / 2 + 0.04 : ut.depth_ft / 2 - 0.04;
    children.push(
      h("mesh", { key: "door",
        position: [dx, -ceilingFt / 2 + 1.6, entryZ],
        rotation: [0, onFront ? Math.PI : 0, 0] },
        h("planeGeometry", { args: [d.width, 3.2] }),
        h("meshStandardMaterial", { color: showEgress ? "#22c55e" : "#0f172a" })
      )
    );
  }

  if (showEgress) {
    ut.rooms.filter(r => r.egress_window).forEach((room, ri) => {
      const r = placeRoom(unit, ut, room);
      const onFront = (r.z) <= unit.z + 0.01;
      const wx = r.x + r.w / 2 - cx;
      children.push(
        h("mesh", { key: `egress-${ri}`,
          position: [wx, 0.55, onFront ? -ut.depth_ft / 2 - 0.05 : ut.depth_ft / 2 + 0.05],
          rotation: [0, onFront ? Math.PI : 0, 0] },
          h("planeGeometry", { args: [2.8, 3.2] }),
          h("meshBasicMaterial", { color: "#ef4444", transparent: true, opacity: 0.55 })
        )
      );
    });
  }

  if (isActive && showUnitLabels) {
    children.push(
      h(Html, { key: "label", position: [0, ceilingFt / 2 + 1, 0], center: true, distanceFactor: 50 },
        h("div", { className: "text-[10px] text-amber-300 font-mono bg-[#0b0f14]/80 px-1.5 py-0.5 border border-amber-400/40 rounded whitespace-nowrap" }, unit.label)
      )
    );
  }

  return h("group", { key, position: [cx, cy, cz] }, ...children);
}

// ----- Continuous catwalk (entry side) with full-length canopy -----
function catwalkAndCanopy(wingLen, wingDepth, slabTop, ceilingFt, balconyOnFront,
                          isTopFloor, key) {
  // Catwalk side = opposite of balcony side.
  // balconyOnFront=true → balcony at z<0, catwalk at z>D side.
  const walkDepth = 5;
  const xOverhang = 4;
  const walkLen = wingLen + xOverhang * 2;
  const walkCx = wingLen / 2;
  const sign = balconyOnFront ? 1 : -1;
  const walkCz = balconyOnFront
    ? wingDepth + walkDepth / 2
    : -walkDepth / 2;

  const items = [];
  // Catwalk slab
  items.push(
    h("mesh", { key: "slab", position: [walkCx, slabTop + 0.15, walkCz] },
      h("boxGeometry", { args: [walkLen, 0.3, walkDepth] }),
      h("meshStandardMaterial", { color: "#475569", roughness: 0.8 })
    )
  );
  // Outer railing edge z position
  const railZ = walkCz + sign * (walkDepth / 2);
  items.push(
    h("mesh", { key: "rail-top", position: [walkCx, slabTop + 3.5, railZ] },
      h("boxGeometry", { args: [walkLen, 0.12, 0.12] }),
      h("meshStandardMaterial", { color: "#1e293b" })
    )
  );
  const postCount = Math.floor(walkLen / 8);
  for (let i = 0; i <= postCount; i++) {
    const px = -walkLen / 2 + (i / postCount) * walkLen;
    items.push(
      h("mesh", { key: `post-${i}`, position: [walkCx + px, slabTop + 1.8, railZ] },
        h("boxGeometry", { args: [0.1, 3.6, 0.1] }),
        h("meshStandardMaterial", { color: "#1e293b" })
      )
    );
  }

  // Canopy roof above this catwalk on the TOP floor only
  if (isTopFloor) {
    const roofDepth = walkDepth + 1.5;
    const roofCz = walkCz - sign * 0.5;        // pull canopy a touch toward unit edge
    const roofY = slabTop + ceilingFt + 0.4;
    items.push(
      h("mesh", { key: "roof", position: [walkCx, roofY, roofCz] },
        h("boxGeometry", { args: [walkLen, 0.25, roofDepth] }),
        h("meshStandardMaterial", { color: "#1e293b", metalness: 0.5, roughness: 0.4 })
      )
    );
    items.push(
      h("mesh", { key: "fascia", position: [walkCx, roofY - 0.45, railZ + sign * 0.05] },
        h("boxGeometry", { args: [walkLen, 0.7, 0.12] }),
        h("meshStandardMaterial", { color: "#fbbf24", emissive: "#f59e0b", emissiveIntensity: 0.2 })
      )
    );
  }

  return h("group", { key }, ...items);
}

// ----- Individual unit balcony on the courtyard side, with canopy on top floor -----
function unitBalcony(unit, ut, slabTop, balconyOnFront, key) {
  const cx = unit.x + ut.length_ft / 2;
  const balconyDepth = 4;
  const sign = balconyOnFront ? -1 : 1;
  const balCz = balconyOnFront
    ? -balconyDepth / 2
    : ut.depth_ft + balconyDepth / 2;

  const items = [];
  items.push(
    h("mesh", { key: "platform", position: [0, slabTop + 0.1, sign * balconyDepth / 2 + (balconyOnFront ? 0 : ut.depth_ft)] },
      h("boxGeometry", { args: [ut.length_ft - 1, 0.3, balconyDepth] }),
      h("meshStandardMaterial", { color: "#475569", roughness: 0.8 })
    )
  );
  // Railing on the outer edge of the balcony
  const railZ = balconyOnFront ? -balconyDepth - 0.05 : ut.depth_ft + balconyDepth + 0.05;
  items.push(
    h("mesh", { key: "rail-top", position: [0, slabTop + 3.2, railZ] },
      h("boxGeometry", { args: [ut.length_ft - 1, 0.1, 0.1] }),
      h("meshStandardMaterial", { color: "#1e293b" })
    )
  );
  for (let i = 0; i < 4; i++) {
    const px = -ut.length_ft / 2 + (i + 0.5) * ((ut.length_ft - 1) / 4);
    items.push(
      h("mesh", { key: `post-${i}`, position: [px, slabTop + 1.6, railZ] },
        h("boxGeometry", { args: [0.1, 3.2, 0.1] }),
        h("meshStandardMaterial", { color: "#1e293b" })
      )
    );
  }

  return h("group", { key, position: [cx, 0, 0] }, ...items);
}

// ----- Balcony-side canopy on the top floor (covers all balconies along the wing) -----
function balconyCanopy(wingLen, wingDepth, slabTop, ceilingFt, balconyOnFront, key) {
  const balconyDepth = 4;
  const canopyLen = wingLen + 4;
  const canopyDepth = balconyDepth + 1.5;
  const canopyCx = wingLen / 2;
  const canopyCz = balconyOnFront
    ? -balconyDepth / 2 - 0.5
    : wingDepth + balconyDepth / 2 + 0.5;
  const roofY = slabTop + ceilingFt + 0.4;
  const sign = balconyOnFront ? -1 : 1;

  const items = [
    h("mesh", { key: "slab", position: [canopyCx, roofY, canopyCz] },
      h("boxGeometry", { args: [canopyLen, 0.25, canopyDepth] }),
      h("meshStandardMaterial", { color: "#1e293b", metalness: 0.5, roughness: 0.4 })
    ),
    h("mesh", { key: "fascia", position: [canopyCx, roofY - 0.45, canopyCz + sign * (canopyDepth / 2 + 0.05)] },
      h("boxGeometry", { args: [canopyLen, 0.7, 0.12] }),
      h("meshStandardMaterial", { color: "#fbbf24", emissive: "#f59e0b", emissiveIntensity: 0.2 })
    ),
  ];
  return h("group", { key }, ...items);
}

// ----- Floor slab + units + walkway + balconies -----
function wingFloor(wing, floor, unitTypes, yBase, transparent, showEgress, isActive,
                   showBalconies, isTopFloor, storefronts, showUnitLabels = true) {
  const slab = 0.6;
  const W = wing.length_ft;
  const D = wing.depth_ft;
  const balconyOnFront = wing.balcony_side === "front";
  const items = [
    h("mesh", { key: "slab", position: [W / 2, slab / 2, D / 2] },
      h("boxGeometry", { args: [W + 2, slab, D + 0.4] }),
      h("meshStandardMaterial", { color: "#111827", roughness: 0.9 })
    ),
  ];

  // Exterior catwalk for upper floors (single-loaded entry corridor)
  if (floor.level >= 2) {
    items.push(catwalkAndCanopy(W, D, slab, floor.ceiling_ft, balconyOnFront,
                                isTopFloor, `catwalk-${floor.level}`));
  } else {
    // Ground-floor entry canopy on the catwalk side
    items.push(catwalkAndCanopy(W, D, slab, floor.ceiling_ft, balconyOnFront,
                                false, `gnd-catwalk-${floor.level}`));
  }

  // Balcony canopy on top floor (covers the balcony side too)
  if (isTopFloor && showBalconies) {
    items.push(balconyCanopy(W, D, slab, floor.ceiling_ft, balconyOnFront,
                             `bal-canopy-${floor.level}`));
  }

  floor.units.forEach((u, idx) => {
    const ut = unitTypes[u.unit_type_id];
    if (!ut) return;
    const hasBal = showBalconies && floor.level >= 2 && ut.id !== "COMM_40";
    // Storefront index from x position (each storefront is 32 ft wide, starting at x=0)
    const isStore = ut.id === "COMM_40";
    const storeIdx = isStore ? Math.round(u.x / 32.0) : -1;
    const override = isStore && storefronts && storeIdx >= 0 && storeIdx < storefronts.length
      ? storefronts[storeIdx]
      : null;
    items.push(unitBox(u, ut, floor.ceiling_ft, slab, transparent, showEgress, isActive,
                       hasBal, balconyOnFront, `u-${floor.level}-${idx}`,
                       override ? override.color : null, showUnitLabels));
    if (hasBal) {
      items.push(unitBalcony(u, ut, slab, balconyOnFront, `bal-${floor.level}-${idx}`));
    }
  });
  return h("group", { key: `floor-${floor.level}`, position: [0, yBase, 0] }, ...items);
}

function stairwellMesh(s, totalHeight, key) {
  const stepCount = 14;
  const stepRise = totalHeight / stepCount;
  const stepRun = s.d / stepCount;
  const items = [
    h("mesh", { key: "frame", position: [0, totalHeight / 2, 0] },
      h("boxGeometry", { args: [s.w, totalHeight, s.d] }),
      h("meshStandardMaterial", { color: "#0f172a", transparent: true, opacity: 0.25 })
    ),
  ];
  for (let i = 0; i < stepCount; i++) {
    items.push(
      h("mesh", { key: `step-${i}`,
        position: [0, stepRise * (i + 0.5), -s.d / 2 + stepRun * (i + 0.5)] },
        h("boxGeometry", { args: [Math.max(s.w - 1, 1), Math.max(stepRise * 0.6, 0.2), Math.max(stepRun * 0.95, 0.2)] }),
        h("meshStandardMaterial", { color: "#facc15", emissive: "#f59e0b", emissiveIntensity: 0.2 })
      )
    );
  }
  items.push(
    h(Html, { key: "label", position: [0, totalHeight + 2, 0], center: true, distanceFactor: 60 },
      h("div", { className: "text-[10px] text-amber-300 font-mono bg-[#0b0f14]/85 px-2 py-0.5 border border-amber-400/40 rounded tracking-widest whitespace-nowrap" }, s.label)
    )
  );
  return h("group", { key, position: [s.x + s.w / 2, 0, s.z + s.d / 2] }, ...items);
}

// ----- One wing rendered at site coordinates -----
function wingGroup(wing, activeFloor, showEgress, transparent, showBalconies, showSolar, key, storefronts, showUnitLabels = true) {
  const unitTypes = Object.fromEntries(
    (wing._unit_types || []).map(u => [u.id, u])
  );
  let cumY = 0;
  const totalHeight = wing.floors.reduce((s, f) => s + f.ceiling_ft + 0.6, 0);
  const items = [];

  wing.floors.forEach((f, fi) => {
    const yBase = cumY;
    cumY += f.ceiling_ft + 0.6;
    const isTop = fi === wing.floors.length - 1;
    items.push(wingFloor(wing, f, unitTypes, yBase, transparent, showEgress,
                         f.level === activeFloor, showBalconies, isTop, storefronts, showUnitLabels));
  });

  wing.stairwells.forEach((s, i) => {
    items.push(stairwellMesh(s, totalHeight, `stair-${i}`));
  });

  // Solar PV: tile the top of the catwalk canopy on the entry side, length-wise
  if (showSolar) {
    const W = wing.length_ft;
    const D = wing.depth_ft;
    const balconyOnFront = wing.balcony_side === "front";
    const roofY = totalHeight + 0.5;
    const walkDepth = 5;
    const walkCz = balconyOnFront ? D + walkDepth / 2 : -walkDepth / 2;
    const cols = Math.max(4, Math.floor((W + 8) / 4));
    for (let i = 0; i < cols; i++) {
      const px = (i + 0.5) * ((W + 8) / cols) - 4;
      [0, 1].forEach(j => {
        const pz = walkCz - walkDepth / 2 + (j + 0.5) * (walkDepth / 2);
        items.push(
          h("mesh", { key: `pv-w-${i}-${j}`, position: [px, roofY, pz], rotation: [-Math.PI / 2, 0, 0] },
            h("planeGeometry", { args: [((W + 8) / cols) * 0.94, (walkDepth / 2) * 0.88] }),
            h("meshStandardMaterial", { color: "#0b1e3f", emissive: "#1e3a8a", emissiveIntensity: 0.4, metalness: 0.85, roughness: 0.2 })
          )
        );
      });
    }
    // Also over the balcony canopy
    const balCz = balconyOnFront ? -2 - 0.5 : D + 2 + 0.5;
    const bcCols = Math.max(4, Math.floor((W + 4) / 4));
    for (let i = 0; i < bcCols; i++) {
      const px = (i + 0.5) * ((W + 4) / bcCols) - 2;
      [0, 1].forEach(j => {
        const pz = balCz - 2.75 + (j + 0.5) * 2.75;
        items.push(
          h("mesh", { key: `pv-b-${i}-${j}`, position: [px, roofY, pz], rotation: [-Math.PI / 2, 0, 0] },
            h("planeGeometry", { args: [((W + 4) / bcCols) * 0.94, 2.4] }),
            h("meshStandardMaterial", { color: "#0b1e3f", emissive: "#1e3a8a", emissiveIntensity: 0.4, metalness: 0.85, roughness: 0.2 })
          )
        );
      });
    }
  }

  const rotRad = (wing.rotation_deg || 0) * Math.PI / 180;
  return h("group", { key,
    position: [wing.origin_x, 0, wing.origin_z],
    rotation: [0, rotRad, 0] },
    ...items
  );
}

function buildScene(building, activeFloor, showEgress, transparent, showBalconies, showSolar, preset, storefronts) {
  // LOD: hide per-unit labels on the whole-site composite (too dense — 14 wings × multiple
  // floors of units would render ~100+ labels). Users drill into individual buildings for detail.
  const showUnitLabels = building.kind !== "site_composite";
  // Inject unit_types into each wing for fast lookup
  const wings = building.wings.map(w => ({ ...w, _unit_types: building.unit_types }));
  const extras = building.site_extras || [];

  const nodes = [];
  nodes.push(h(CameraController, { key: "cam-ctrl", preset }));
  nodes.push(h("ambientLight", { key: "amb", intensity: 0.6 }));
  nodes.push(h("directionalLight", { key: "dir1", position: [120, 180, 100], intensity: 1.15 }));
  nodes.push(h("directionalLight", { key: "dir2", position: [-80, 80, -60], intensity: 0.45, color: "#7dd3fc" }));

  nodes.push(
    h("mesh", { key: "ground", rotation: [-Math.PI / 2, 0, 0], position: [building.width_ft / 2, -0.05, building.depth_ft / 2] },
      h("planeGeometry", { args: [1200, 1200] }),
      h("meshStandardMaterial", { color: "#050a10" })
    )
  );

  // Grid lines
  for (let i = -20; i < 80; i++) {
    nodes.push(
      h("mesh", { key: `gx-${i}`, position: [i * 10, 0.01, building.depth_ft / 2], rotation: [-Math.PI / 2, 0, 0] },
        h("planeGeometry", { args: [0.06, 800] }),
        h("meshBasicMaterial", { color: "#1e293b", transparent: true, opacity: 0.45 })
      )
    );
  }
  for (let i = -10; i < 80; i++) {
    nodes.push(
      h("mesh", { key: `gz-${i}`, position: [building.width_ft / 2, 0.01, i * 10], rotation: [-Math.PI / 2, 0, 0] },
        h("planeGeometry", { args: [1200, 0.06] }),
        h("meshBasicMaterial", { color: "#1e293b", transparent: true, opacity: 0.45 })
      )
    );
  }

  // Site extras (pavilion / green / laundry / parking / garden / drop_off / gateway_arch)
  extras.forEach((ex, ei) => {
    const cx = ex.x + ex.w / 2;
    const cz = ex.z + ex.d / 2;
    const cy = ex.h / 2;
    const isPavilion = ex.kind === "pavilion";
    const isGreen = ex.kind === "green" || ex.kind === "garden";
    const isGateway = ex.kind === "gateway_arch";
    const isDropOff = ex.kind === "drop_off";

    if (isGateway) {
      // Steel arch — two vertical posts + a horizontal beam + a glowing sign band
      const postW = 1.5, postD = 1.5;
      const beamH = 1.2;
      const signH = 2.6;
      nodes.push(
        // Left post
        h("mesh", { key: `gw-${ei}-pL`, position: [ex.x + postW / 2, ex.h / 2, cz] },
          h("boxGeometry", { args: [postW, ex.h, postD] }),
          h("meshStandardMaterial", { color: "#0f172a", metalness: 0.85, roughness: 0.25 })
        ),
        // Right post
        h("mesh", { key: `gw-${ei}-pR`, position: [ex.x + ex.w - postW / 2, ex.h / 2, cz] },
          h("boxGeometry", { args: [postW, ex.h, postD] }),
          h("meshStandardMaterial", { color: "#0f172a", metalness: 0.85, roughness: 0.25 })
        ),
        // Top beam
        h("mesh", { key: `gw-${ei}-beam`, position: [cx, ex.h - beamH / 2, cz] },
          h("boxGeometry", { args: [ex.w, beamH, postD] }),
          h("meshStandardMaterial", { color: "#0f172a", metalness: 0.85, roughness: 0.25 })
        ),
        // Glowing amber sign band ("SUNRISE COMMUNITY")
        h("mesh", { key: `gw-${ei}-sign`, position: [cx, ex.h - beamH - signH / 2, cz] },
          h("boxGeometry", { args: [ex.w * 0.72, signH, 0.4] }),
          h("meshStandardMaterial", {
            color: "#fbbf24", emissive: "#f59e0b", emissiveIntensity: 0.95,
            metalness: 0.4, roughness: 0.4,
          })
        ),
      );
      nodes.push(
        h(Html, { key: `gw-${ei}-lbl`, position: [cx, ex.h - beamH - signH / 2, cz + postD / 2 + 0.1], center: true, distanceFactor: 60 },
          h("div", {
            className: "text-black font-extrabold text-[12px] tracking-[0.35em] whitespace-nowrap px-3 py-0.5 bg-amber-400 rounded"
          }, ex.label)
        )
      );
      return;
    }

    const isOverlay = ex.kind === "parking" || ex.kind === "plaza_wrap" || isDropOff;
    let extraOpacity = 1;
    if (isGreen) extraOpacity = 0.6;
    else if (isOverlay) extraOpacity = 0.85;

    nodes.push(
      h("mesh", { key: `ex-${ei}`, position: [cx, cy, cz] },
        h("boxGeometry", { args: [ex.w, ex.h, ex.d] }),
        h("meshStandardMaterial", {
          color: ex.color,
          metalness: isGreen ? 0 : 0.4, roughness: isGreen ? 0.95 : 0.5,
          transparent: isGreen || isOverlay,
          opacity: extraOpacity,
        })
      )
    );
    // Pavilion roof overhang
    if (isPavilion) {
      nodes.push(
        h("mesh", { key: `ex-${ei}-roof`, position: [cx, ex.h + 0.4, cz] },
          h("boxGeometry", { args: [ex.w + 6, 0.6, ex.d + 6] }),
          h("meshStandardMaterial", { color: "#0f172a", metalness: 0.6, roughness: 0.3 })
        )
      );
      nodes.push(
        h("mesh", { key: `ex-${ei}-fascia`, position: [cx, ex.h - 0.3, cz + ex.d / 2 + 0.1] },
          h("boxGeometry", { args: [ex.w + 6, 0.7, 0.12] }),
          h("meshStandardMaterial", { color: "#fbbf24", emissive: "#f59e0b", emissiveIntensity: 0.25 })
        )
      );
    }
    if (ex.label && (isPavilion || ex.kind === "laundry")) {
      nodes.push(
        h(Html, { key: `ex-${ei}-lbl`, position: [cx, ex.h + 4, cz], center: true, distanceFactor: 80 },
          h("div", {
            className: "text-amber-300 font-mono text-[10px] tracking-widest bg-[#0b0f14]/80 px-2 py-0.5 border border-amber-400/40 rounded whitespace-nowrap"
          }, ex.label)
        )
      );
    }
  });

  wings.forEach((w, i) => {
    nodes.push(wingGroup(w, activeFloor, showEgress, transparent, showBalconies, showSolar, `wing-${i}`, storefronts, showUnitLabels));
  });

  // Compass
  nodes.push(
    h(Html, { key: "compass", position: [-20, 2, -10], center: true, distanceFactor: 80 },
      h("div", { className: "text-amber-400 font-mono text-[10px] tracking-widest border border-amber-400/50 rounded px-2 py-1 bg-[#0b0f14]/80 whitespace-nowrap" }, "N ↑")
    )
  );

  nodes.push(
    h(OrbitControls, {
      key: "controls",
      target: preset?.target || [building.width_ft / 2, 14, building.depth_ft / 2],
      maxPolarAngle: Math.PI / 2 - 0.02,
      minDistance: 30,
      maxDistance: 1500,
      makeDefault: true,
    })
  );

  return h(F, null, ...nodes);
}

import { useStorefrontOverrides } from "@/hooks/useStorefrontOverrides";

export default function Building3D({ building, activeFloor, showEgress, transparent, showBalconies, showSolar, preset }) {
  const storefronts = useStorefrontOverrides();
  // buildScene is a module-level function (stable reference) — safe to omit from deps.
  const scene = useMemo(
    () => buildScene(building, activeFloor, showEgress, transparent, showBalconies, showSolar, preset, storefronts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [building, activeFloor, showEgress, transparent, showBalconies, showSolar, preset, storefronts]
  );
  return h(
    "div",
    { "data-testid": "building-3d-canvas", className: "w-full h-[640px] bg-gradient-to-b from-[#070b10] to-[#0b1220]" },
    h(Canvas, {
      camera: { position: preset?.pos || [200, 180, 350], fov: 45 },
      gl: { preserveDrawingBuffer: true, antialias: true },
    },
      h(Suspense, { fallback: null }, scene)
    )
  );
}
